import { faChevronLeft, faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, FormControlLabel, OutlinedInput, Radio, RadioGroup, Typography } from "@mui/material"
import { FormikProps, useFormik } from "formik"
import moment from "moment"
import { useState } from "react"
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext"
import TypographyAccessibility from "../../../components/Accessibility/TypographyAccessibility"
import { RegisterFormProps } from "../../../interfaces/props/register/RegisterFormProps"
import { OtherUnimedRegisterFormValidation } from "../../../interfaces/validation/OtherUnimedRegisterFormValidation"
import { RegisterUnimedCampinasValidation } from "../../../interfaces/validation/RegisterUnimedCampinasValidation"
import { cpfMask } from "../../../utils/CpfMask"
import { onlyNumbers } from "../../../utils/OnlyNumbers"
import "../styles.css"
import { sxButtonLogin, sxButtonText, sxLabel, sxTypographyTitle, sxValidationMessage } from "../sxProps"
import DateInput from "../../../components/DateInput"
import dayjs from "dayjs"
import LightTooltip from "../../../components/LightTooltip"
import { faCircleInfo } from "@fortawesome/pro-duotone-svg-icons"


const RegisterForm: React.FC<RegisterFormProps> = ({ openRegisterValidationData, backToLogin }) => {

  const [customerType, setCustomerType] = useState('campinas');

  const formik = useFormik({
    initialValues: { dataNascimento: '', cpf: '', numeroCartao: '' },
    validationSchema: customerType === 'campinas' ? RegisterUnimedCampinasValidation : OtherUnimedRegisterFormValidation,
    onSubmit: (values) => {
      openRegisterValidationData(values.dataNascimento, values.cpf, values.numeroCartao)
    },
    validateOnChange: false
  })

  const { textStyleAccessibility, buttonStyleAccessibility, buttonTextStyleAccessibility, contrastMode } = useAccessibility();

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    mask: (inputValue: string) => string | null,
    formik: FormikProps<any>
  ) => {
    const { value } = (event.target as HTMLInputElement);
    formik.setFieldValue(field, mask ? mask(value) : value);
  };

  return (
    <form style={textStyleAccessibility} className='width-100 ' onSubmit={formik.handleSubmit}>
      <div className='password-recovery-container-text margin-bottom-20'>
        <Typography style={textStyleAccessibility} sx={sxTypographyTitle} className='margin-bottom-painel-rigth'>Cadastro</Typography>
        <div className='container-text-recovery '>
          <span style={textStyleAccessibility} className='login-text login-text-center'>Preencha os dados abaixo para ter acesso ao Canal do Cliente</span>
        </div>
      </div>
      <div className='width-100 margin-bottom-20'>
        <RadioGroup value={customerType} onChange={(e) => {
          formik.setFieldValue('numeroCartao', '');
          setCustomerType(e.target.value);
        }}>
          <FormControlLabel value="campinas" control={<Radio style={{ background: 'white' }} />} label={
            <div className="container-label-info">
              <TypographyAccessibility>Cliente Unimed Campinas</TypographyAccessibility>
              <LightTooltip
                title='Cliente Unimed Campinas é o que possui o plano da Unimed Campinas, com número de cartão iniciado em 0002.'
                placement="right"
              >
                <FontAwesomeIcon icon={faCircleInfo} className={`${contrastMode ? 'fa-icon18x18-accessibility' : 'fa-icon18x18'}`} />
              </LightTooltip>
            </div>
          } />
          <FormControlLabel value="other" control={<Radio style={{ background: 'white' }} />} label={
            <div className="container-label-info" >
              <TypographyAccessibility>Cliente outras Unimeds</TypographyAccessibility>
              <LightTooltip
                title='Cliente de Intercâmbio é o que possui cartão de outras Unimeds do sistema, incluindo Unimed Nacional, e que será atendido na rede de atendimento da Unimed Campinas.'
                placement="right"
              >
                <FontAwesomeIcon icon={faCircleInfo} className={`${contrastMode ? 'fa-icon18x18-accessibility' : 'fa-icon18x18'}`} />
              </LightTooltip>
            </div>
          } />
        </RadioGroup>
      </div>
      <div className='width-100'>
        {customerType !== 'campinas' &&
          <div className='width-100 margin-bottom-20'>
            <Typography style={textStyleAccessibility} sx={sxLabel}>Número do cartão</Typography>
            <label htmlFor="cardInput" aria-label="Número do cartão" />
            <OutlinedInput
              fullWidth
              id="cardInput"
              type={'text'}
              placeholder='9999999999999999'
              value={formik.values.numeroCartao}
              onChange={(event) => { formik.setFieldValue('numeroCartao', onlyNumbers(event.target.value)) }}
              name='numeroCartao'
              error={!!formik.errors.numeroCartao}
              maxRows={1}
              inputProps={{ maxLength: 30 }}
            />
            {formik.errors.numeroCartao &&
              <div className="container-validation-message">
                <FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
                <Typography sx={sxValidationMessage} style={textStyleAccessibility} >{formik.errors.numeroCartao}</Typography>
              </div>
            }
          </div>
        }
        <div className="register-form-row gap-24">
          <div className='width-100 margin-bottom-20'>
            <Typography style={textStyleAccessibility} sx={sxLabel}>CPF</Typography>
            <label htmlFor="cpfInput" aria-label="CPF" />
            <OutlinedInput
              fullWidth
              id="cpfInput"
              name="cpf"
              inputProps={{ maxLength: 14 }}
              error={!!formik?.errors?.cpf}
              type={'text'}
              inputMode="numeric"
              value={formik?.values?.cpf ?? ""}
              placeholder='999.999.999-99'
              onChange={(e) => handleInputChange(e, 'cpf', cpfMask, formik)}
            />
            {formik.errors.cpf &&
              <div className="container-validation-message">
                <FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
                <Typography sx={sxValidationMessage} style={textStyleAccessibility} >{formik.errors.cpf}</Typography>
              </div>
            }
          </div>
          <div className='width-100'>
            <Typography style={textStyleAccessibility} sx={sxLabel}>Data de nascimento</Typography>
            <DateInput
              label="Data de nascimento"
              maxDate={dayjs(moment().format('YYYY-MM-DD'))}
              minDate={dayjs("1900-01-01")}
              slotProps={{
                textField: {
                  placeholder: '01/01/2000',
                  error: !!formik.errors.dataNascimento,
                },
              }}
              onChange={(newDate) => {
                if (!newDate) {
                  formik.setFieldValue('dataNascimento', '');
                } else {
                  formik.setFieldValue('dataNascimento', dayjs(newDate).format('DD/MM/YYYY'));
                }

              }} />
            {formik.errors.dataNascimento &&
              <div className="container-validation-message">
                <FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
                <Typography sx={sxValidationMessage} style={textStyleAccessibility} >{formik?.errors?.dataNascimento}</Typography>
              </div>
            }
          </div>
        </div>
      </div>
      <div className='recovery-container-action'>
        <Button style={buttonStyleAccessibility} type='submit' variant='contained' sx={sxButtonLogin}>
          <Typography style={buttonTextStyleAccessibility}>Continuar</Typography>
        </Button>
        <Button
          onClick={backToLogin}
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        >
          <Typography style={textStyleAccessibility} sx={sxButtonText}>Voltar</Typography>
        </Button>
      </div>
    </form >
  )
}

export default RegisterForm